/**
 * Central demo date utility — Asia/Kuala_Lumpur timezone
 * All demo timestamps are calculated relative to the current local date.
 */

const TZ = 'Asia/Kuala_Lumpur';

export function getDemoNow(): Date {
  return new Date();
}

export function formatDemoDate(date: Date): string {
  return date.toLocaleDateString('en-MY', {
    timeZone: TZ,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatDemoShortDate(date: Date): string {
  return date.toLocaleDateString('en-MY', {
    timeZone: TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDemoTime(date: Date): string {
  return date.toLocaleTimeString('en-MY', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Returns a date offset from today (negative = past, positive = future) */
export function demoDateOffset(days: number): Date {
  const d = getDemoNow();
  d.setDate(d.getDate() + days);
  return d;
}

export function relativeLabel(days: number): string {
  if (days === 0) return 'Today';
  if (days === -1) return 'Yesterday';
  if (days === -2) return '2 days ago';
  if (days === -3) return '3 days ago';
  if (days > 0) return `In ${days} day${days > 1 ? 's' : ''}`;
  return `${Math.abs(days)} days ago`;
}

/** Short date string like "16 Aug 2026" relative to today */
export function demoShortDateOffset(days: number): string {
  return formatDemoShortDate(demoDateOffset(days));
}

/** Today's date formatted for dashboard header */
export function getDashboardDate(): string {
  return formatDemoDate(getDemoNow());
}

/** Current time HH:MM */
export function getDemoCurrentTime(): string {
  return formatDemoTime(getDemoNow());
}

/** MIR reference based on today */
export function getMIRRef(seq: number): string {
  const now = getDemoNow();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `MIR-${y}${m}${d}-${seq}`;
}

/** MIR Screen Recording reference */
export function getMIRScreenRef(seq: number): string {
  const now = getDemoNow();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `MIR-SCR-${y}${m}${d}-${seq}`;
}

/** Interaction ID */
export function getInteractionId(seq: number): string {
  const now = getDemoNow();
  return `INT-${now.getFullYear()}-${seq}`;
}

/** Case ID */
export function getCaseId(seq: number): string {
  const now = getDemoNow();
  return `MYGCC-${now.getFullYear()}-${String(seq).padStart(6, '0')}`;
}
